import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Send, Mail, Eye, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AdminTabs from "@/components/AdminTabs";

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function AdminNewsletterPage() {
  const [count, setCount] = useState(null);
  const [preview, setPreview] = useState(null);
  const [days, setDays] = useState(7);
  const [testEmail, setTestEmail] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/newsletter/count").then((r) => setCount(r.data?.count ?? 0)).catch(() => setCount(0));
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout", null, { withCredentials: true }); } catch { /* ignore */ }
    window.location.href = "/login";
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const r = await api.get(`/admin/newsletter/preview-digest?days=${days}`, { withCredentials: true });
      setPreview(r.data);
    } catch { toast.error("Failed to load preview"); } finally { setLoadingPreview(false); }
  };

  const sendDigest = async () => {
    if (!count) { toast.error("No subscribers yet."); return; }
    if (!window.confirm(`Send digest to ${count} subscriber${count === 1 ? "" : "s"}?`)) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/newsletter/send-digest?days=${days}`, null, { withCredentials: true });
      toast.success(`Sent ${r.data.sent}/${r.data.recipients_total} · articles ${r.data.articles_count} · filings ${r.data.filings_count}`);
    } catch { toast.error("Send failed"); } finally { setSending(false); }
  };

  const testSend = async () => {
    if (!testEmail.trim()) { toast.error("Enter an email"); return; }
    setTesting(true);
    try {
      await api.post("/admin/newsletter/test-send", { to: testEmail.trim() }, { withCredentials: true });
      toast.success(`Welcome email sent to ${testEmail.trim()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed — check Resend domain verification");
    } finally { setTesting(false); }
  };

  return (
    <div data-testid="admin-newsletter-page" className="bg-bb-offwhite min-h-screen">
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="bb-container py-3 flex items-center justify-between gap-3">
          <div>
            <div className="bb-overline text-bb-copper">Admin · Newsletter</div>
            <div className="font-archivo font-bold text-bb-navy text-lg leading-tight">
              {count === null ? "…" : `${count} subscriber${count === 1 ? "" : "s"}`}
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout"
            className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-archivo uppercase tracking-widest text-bb-slate hover:text-bb-navy">
            <LogOut size={13} /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
        <AdminTabs />
      </header>

      <div className="bb-container py-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <section className="bg-white border border-border p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="bb-overline text-bb-copper">Weekly digest</div>
                <div className="font-archivo text-bb-navy text-base font-semibold">Send now or preview</div>
              </div>
              <div className="flex items-center gap-2">
                <label className="bb-overline text-bb-slate">Window</label>
                <select
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value, 10))}
                  data-testid="newsletter-days"
                  className="border border-border px-2 py-2 text-xs font-mono bg-white"
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>
            </div>
            <p className="text-sm text-bb-slate leading-relaxed">
              Pulls the top articles + open-comment regulatory filings from the past {days} days.
              Cron runs automatically every Monday 07:00 ET.
            </p>
            <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
              <button
                onClick={loadPreview}
                disabled={loadingPreview}
                data-testid="newsletter-preview"
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-border text-bb-navy text-sm font-archivo uppercase tracking-widest disabled:opacity-50 min-h-[44px]"
              >
                {loadingPreview ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview
              </button>
              <button
                onClick={sendDigest}
                disabled={sending || !count}
                data-testid="newsletter-send"
                className="bb-btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm disabled:opacity-50 min-h-[44px]"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send to {count ?? 0} subscriber{count === 1 ? "" : "s"}
              </button>
            </div>
          </section>

          {preview && (
            <section className="bg-white border border-border overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div>
                  <div className="bb-overline text-bb-copper">Preview · {preview.week_label}</div>
                  <div className="text-xs font-mono text-bb-slate">
                    articles {preview.articles.length} · open-comment filings {preview.filings.length}
                  </div>
                </div>
                <span className="text-[11px] font-mono text-bb-slate">rendered {(preview.html.length / 1024).toFixed(1)} kB</span>
              </div>
              <iframe
                data-testid="newsletter-preview-iframe"
                title="Digest preview"
                srcDoc={preview.html}
                className="w-full border-0"
                style={{ height: "560px" }}
              />
            </section>
          )}
        </div>

        <aside className="lg:col-span-1 space-y-4">
          <section className="bg-white border border-border p-4 space-y-3">
            <div className="bb-overline text-bb-copper">Send test welcome</div>
            <p className="text-xs text-bb-slate leading-relaxed">
              Fires the exact welcome email new subscribers receive. Useful to verify Resend deliverability and DNS.
            </p>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              data-testid="newsletter-test-email"
              placeholder="you@example.com"
              className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-bb-copper"
            />
            <button
              onClick={testSend}
              disabled={testing || !testEmail}
              data-testid="newsletter-test-send"
              className="w-full bb-btn-primary inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm disabled:opacity-50 min-h-[44px]"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send test welcome
            </button>
          </section>

          <section className="bg-white border border-border p-4 space-y-2">
            <div className="bb-overline text-bb-copper">Sender</div>
            <div className="text-xs font-mono text-bb-slate leading-relaxed break-all">
              Configured in <code>SENDER_EMAIL</code> and <code>SENDER_NAME</code> env vars.
              To send from <code>hello@bedrockbulletin.ca</code>, verify DNS records at{" "}
              <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-bb-navy bb-link inline-flex items-center gap-1">
                resend.com/domains <ExternalLink size={10} />
              </a>{" "}
              then flip the env var.
            </div>
          </section>

          <div className="text-center">
            <Link to="/" className="text-xs font-archivo uppercase tracking-widest text-bb-slate bb-link">Back to site</Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
